import { useMemo, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { ISLANDS, SEYCHELLES_DISTRICTS, type Address } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function Addresses() {
  const { persona } = useAuth();
  const canOfficer = persona?.roles.includes('ADDRESS_OFFICER') ?? false;
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [retiring, setRetiring] = useState<Address | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (q) p.set('q', q);
    if (status) p.set('status', status);
    return `/api/v1/geoserve/addresses?${p}`;
  }, [q, status]);
  const listQ = useApiQuery<{ items: Address[] }>(query);

  const approve = async (a: Address) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/geoserve/addresses/${a.id}/approve`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Approve failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Address register"
        subtitle="Search the national address register; assign draft addresses once verified"
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Draft address
          </button>
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input max-w-xs" placeholder="Search street or locality…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input max-w-[160px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="ASSIGNED">Assigned</option>
          <option value="RETIRED">Retired</option>
        </select>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No addresses found" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Street</th>
                <th className="table-head">Locality</th>
                <th className="table-head">District</th>
                <th className="table-head">Island</th>
                <th className="table-head">Parcel</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{a.street}</td>
                  <td className="table-cell">{a.locality}</td>
                  <td className="table-cell text-xs">{a.district}</td>
                  <td className="table-cell text-xs">{a.island.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{a.parcelNo ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="table-cell text-right">
                    {canOfficer && a.status === 'DRAFT' ? (
                      <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => approve(a)}>
                        Approve
                      </button>
                    ) : null}
                    {canOfficer && a.status === 'ASSIGNED' ? (
                      <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setRetiring(a)}>
                        Retire
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {retiring ? (
        <RetireModal
          address={retiring}
          onClose={() => setRetiring(null)}
          onDone={() => {
            setRetiring(null);
            listQ.reload();
          }}
          onError={setActionError}
        />
      ) : null}
    </div>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState<{
    street: string;
    locality: string;
    district: string;
    island: (typeof ISLANDS)[number];
    parcelNo: string;
    lng: string;
    lat: string;
  }>({ street: '', locality: '', district: SEYCHELLES_DISTRICTS[0], island: 'MAHE', parcelNo: '', lng: '', lat: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const lng = Number.parseFloat(form.lng);
      const lat = Number.parseFloat(form.lat);
      if (Number.isNaN(lng) || Number.isNaN(lat)) throw new Error('Longitude/latitude must be numbers');
      await api.post('/api/v1/geoserve/addresses', {
        street: form.street,
        locality: form.locality,
        district: form.district,
        island: form.island,
        parcelNo: form.parcelNo || undefined,
        geometry: { type: 'Point', coordinates: [lng, lat] },
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : e instanceof Error ? e.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Draft a new address" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Street">
            <input className="field-input" value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} />
          </Field>
          <Field label="Locality">
            <input className="field-input" value={form.locality} onChange={(e) => setForm({ ...form, locality: e.target.value })} />
          </Field>
          <Field label="District">
            <select className="field-input" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })}>
              {SEYCHELLES_DISTRICTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Island">
            <select
              className="field-input"
              value={form.island}
              onChange={(e) => setForm({ ...form, island: e.target.value as (typeof ISLANDS)[number] })}
            >
              {ISLANDS.map((i) => (
                <option key={i} value={i}>
                  {i.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Parcel no. (optional)">
            <input className="field-input" value={form.parcelNo} onChange={(e) => setForm({ ...form, parcelNo: e.target.value })} />
          </Field>
          <div />
          <Field label="Longitude">
            <input className="field-input" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="55.4920" />
          </Field>
          <Field label="Latitude">
            <input className="field-input" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="-4.6796" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !form.street || !form.locality || !form.lng || !form.lat}>
            {busy ? 'Saving…' : 'Save draft'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RetireModal({
  address,
  onClose,
  onDone,
  onError,
}: {
  address: Address;
  onClose: () => void;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/api/v1/geoserve/addresses/${address.id}/retire`, { reason });
      onDone();
    } catch (e) {
      onError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Retire failed');
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Retire ${address.street}`} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Reason">
          <textarea className="field-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || reason.length < 3}>
            {busy ? 'Retiring…' : 'Retire'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
